/*!

=========================================================
* Argon Design System React - v1.1.2
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-design-system-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)
* Licensed under MIT (https://github.com/creativetimofficial/argon-design-system-react/blob/master/LICENSE.md)

* Coded by Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/
import React, { useState, useEffect, useRef } from "react";
// Only the grid is still Argon's. The card, fields and buttons are now the
// brand-auth primitives — see styles/brand-auth.css.
import { Container, Row, Col } from "reactstrap";
import "../../styles/brand-auth.css";
import { loginUserSessionService } from "../../services/userService"
import { useNavigate } from "react-router-dom";
import DemoNavbar from "components/Navbars/DemoNavbar.js";
import MainFooter from "components/Footers/MainFooter.js";
import { useUserAuth } from "../../context/UserContext";
import { SHOW_GOOGLE_SIGN_IN } from "../../config/googleAuth";
import { OTHER_COUNTRY, docLangFor, legalAcceptance, privacyNoticeFor } from "../../config/legal";
import {
  LegalCheckbox,
  ResidenceNotice,
  SignupConsentLabel,
  SignupCountryField,
  signupCountryNotYet,
} from "components/Legal/LegalConsent";
import { registerErrorKey } from "../../utils/registerErrors";
import useReveal from "hooks/useReveal";
import { useTranslation, Trans } from "react-i18next";

// The field each error is about, by the id of its input.
const FIELD_IDS = {
  name: "register-name",
  email: "register-email",
  password: "register-password",
  confirm: "register-confirm",
  country: "register-country",
  legal: "register-legal",
};
// What the country select points at once a choice shows a notice.
const COUNTRY_NOTICE_ID = "register-country-notice";
// Firebase errors that point at one field.
const ERROR_FIELDS = {
  "register.errors.emailInUse": "email",
  "register.errors.invalidEmail": "email",
  "register.errors.weakPassword": "password",
};

const Register = () => {
  // The card's entrance — see styles/premium.css.
  useReveal();
  const navigate = useNavigate();
  const [state, setState] = useState({
    email: "",
    password: "",
    confirmPassword: "",
    name: "",
    last: "",
    error: null,
    message: null,
  });
  const { signUp, googleSignUp } = useUserAuth();
  const mainRef = useRef(null);
  const { t, i18n } = useTranslation();
  const [error, setError] = useState("");
  // Which field the error is about: it is marked invalid, points at the
  // message and gets focus.
  const [errorField, setErrorField] = useState(null);
  // The 18+ / Terms box. Never pre-ticked.
  const [legalOk, setLegalOk] = useState(false);
  // The country the person lives in: a code from SIGNUP_COUNTRIES,
  // OTHER_COUNTRY, or "" before a choice.
  const [country, setCountry] = useState("");
  const hasCountryNotice = country === "MX" || country === "CR" || country === OTHER_COUNTRY;

  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
    mainRef.current.scrollTop = 0;
  }, []);

  const handleChange = (event) => {
    setState({ ...state, [event.target.name]: event.target.value });
  };

  const fail = (message, field = null) => {
    setError(message);
    setErrorField(field);
    if (field) document.getElementById(FIELD_IDS[field])?.focus();
  };

  // aria-invalid and aria-describedby for one field: its hint, if any, and
  // the error message while the error is about this field.
  const fieldA11y = (field, hintId) => {
    const invalid = Boolean(error) && errorField === field;
    const describedBy = [hintId, invalid && "register-error"].filter(Boolean).join(" ");
    return {
      "aria-invalid": invalid || undefined,
      "aria-describedby": describedBy || undefined,
    };
  };

  // Both ways of signing up need a country we serve and the box ticked; the
  // backend refuses a new profile without the acceptance it records.
  const countryConfirmed = () => {
    if (!country) {
      fail(t("legal.signupCountryRequired"), "country");
      return false;
    }
    if (country === OTHER_COUNTRY) {
      fail(t("legal.signupCountryNotServed"), "country");
      return false;
    }
    // Mexico and Costa Rica stay closed until the legal texts are final and
    // their notice can be shown in full (config/legal.js: offeredSignupCountries,
    // residenceNoticeReady). While closed they are not even in the select.
    const notYet = signupCountryNotYet(t, country);
    if (notYet) {
      fail(notYet, "country");
      return false;
    }
    return true;
  };

  const legalConfirmed = () => {
    if (legalOk) return true;
    fail(t("legal.signupConfirmRequired"), "legal");
    return false;
  };

  const acceptance = () =>
    legalAcceptance({
      residenceCountry: country,
      locale: (i18n?.language || "es").startsWith("en") ? "en" : "es",
      privacyNotice: privacyNoticeFor(country),
    });

  const handleSubmit = async (event) => {
    event.preventDefault();
    const { email, password, confirmPassword, name, last } = state;

    if (!name.trim()) {
      fail(t("register.errorNameRequired"), "name");
      return;
    }

    if (password !== confirmPassword) {
      fail(t("register.errorPasswordMismatch"), "confirm");
      return;
    }

    if (!countryConfirmed() || !legalConfirmed()) return;

    setError("");
    setErrorField(null);

    try {
      const loggedUser = await signUp(email, password, "user", name, last, acceptance());

      await loginUserSessionService(loggedUser.accessToken)
      navigate("/home");

      setState({
        ...state,
        message: "User registered successfully!",
        error: null,
      });
    } catch (error) {
      // setState({ ...state, error: error.message, message: null });
      const key = registerErrorKey(error);
      fail(t(key), ERROR_FIELDS[key]);
    }
  };


  const submitSingUpWithGoogle = async (event) => {
    if (!countryConfirmed() || !legalConfirmed()) return;
    try {
      // alert("Google button clicked SingUp");
      await googleSignUp(acceptance());
      navigate("/home");
      setState({
        ...state,
        message: "User registered successfully!",
        error: null,
      });
    } catch (error) {
      // alert("ALERT ERROR FOR GOOGLE SingUp");
      fail(t(registerErrorKey(error)));
    }
  };


  return (
    <>
      <DemoNavbar />
      <main className="cn-auth" ref={mainRef}>
        <section className="cn-auth-hero cn-grid">
          <span className="cn-grain" aria-hidden="true" />
          <span className="cn-orb cn-orb-1" />
          <span className="cn-orb cn-orb-2" />
          <span className="cn-orb cn-orb-3" />
          <Container>
            <Row className="justify-content-center">
              <Col xs="12" md="10" lg="6" xl="5">
                <div className="cn-card" data-reveal>
                  <div className="cn-card-head">
                    <h1 className="cn-title">{t("register.createAccount")}</h1>
                  </div>

                  {SHOW_GOOGLE_SIGN_IN && (
                    <>
                      <button
                        className="cn-social"
                        type="button"
                        onClick={submitSingUpWithGoogle}
                      >
                        <img
                          alt=""
                          aria-hidden="true"
                          src={
                            require("assets/img/icons/common/google.svg")
                              .default
                          }
                        />
                        <span>{t("register.signinWith")} Google</span>
                      </button>
                      <div className="cn-divider">
                        {t("register.orSigninWith")}
                      </div>
                    </>
                  )}

                  <form onSubmit={handleSubmit} role="form">
                    {/* Name and last name sit side by side — five stacked
                        fields made the card taller than the fold. */}
                    <Row>
                      <Col xs="12" sm="6">
                        <div className="cn-field">
                          <label className="cn-label" htmlFor="register-name">
                            {t("register.name")}
                          </label>
                          <div className="cn-input-wrap">
                            <i className="ni ni-hat-3 cn-input-icon" aria-hidden="true" />
                            <input
                              id="register-name"
                              className="cn-input"
                              name="name"
                              type="text"
                              autoComplete="given-name"
                              required
                              placeholder={t("register.name")}
                              onChange={handleChange}
                              {...fieldA11y("name")}
                            />
                          </div>
                        </div>
                      </Col>
                      <Col xs="12" sm="6">
                        <div className="cn-field">
                          <label className="cn-label" htmlFor="register-last">
                            {t("register.last")}
                          </label>
                          <div className="cn-input-wrap">
                            <i className="ni ni-hat-3 cn-input-icon" aria-hidden="true" />
                            <input
                              id="register-last"
                              className="cn-input"
                              name="last"
                              type="text"
                              autoComplete="family-name"
                              placeholder={t("register.last")}
                              onChange={handleChange}
                            />
                          </div>
                        </div>
                      </Col>
                    </Row>

                    <div className="cn-field">
                      <label className="cn-label" htmlFor="register-email">
                        {t("register.email")}
                      </label>
                      <div className="cn-input-wrap">
                        <i className="ni ni-email-83 cn-input-icon" aria-hidden="true" />
                        <input
                          id="register-email"
                          className="cn-input"
                          name="email"
                          type="email"
                          autoComplete="email"
                          required
                          placeholder={t("register.email")}
                          onChange={handleChange}
                          {...fieldA11y("email")}
                        />
                      </div>
                    </div>

                    <div className="cn-field">
                      <label className="cn-label" htmlFor="register-password">
                        {t("register.password")}
                      </label>
                      <div className="cn-input-wrap">
                        <i className="ni ni-lock-circle-open cn-input-icon" aria-hidden="true" />
                        <input
                          id="register-password"
                          className="cn-input"
                          name="password"
                          type="password"
                          autoComplete="new-password"
                          required
                          placeholder={t("register.password")}
                          onChange={handleChange}
                          {...fieldA11y("password", "register-password-hint")}
                        />
                      </div>
                      <p className="cn-hint" id="register-password-hint">
                        {t("register.passwordHint")}
                      </p>
                    </div>

                    <div className="cn-field">
                      <label className="cn-label" htmlFor="register-confirm">
                        {t("register.confirmPassword")}
                      </label>
                      <div className="cn-input-wrap">
                        <i className="ni ni-lock-circle-open cn-input-icon" aria-hidden="true" />
                        <input
                          id="register-confirm"
                          className="cn-input"
                          name="confirmPassword"
                          type="password"
                          autoComplete="new-password"
                          required
                          placeholder={t("register.confirmPassword")}
                          onChange={handleChange}
                          {...fieldA11y("confirm")}
                        />
                      </div>
                    </div>

                    {/* The country decides which notice shows (Mexico, Costa
                        Rica) and whether we can open an account at all. */}
                    <SignupCountryField
                      last
                      id={FIELD_IDS.country}
                      value={country}
                      onChange={(e) => setCountry(e.target.value)}
                      describedBy={fieldA11y("country", hasCountryNotice ? COUNTRY_NOTICE_ID : null)["aria-describedby"]}
                      invalid={fieldA11y("country")["aria-invalid"]}
                    />
                    {country === OTHER_COUNTRY ? (
                      <p className="cn-hint" id={COUNTRY_NOTICE_ID}>{t("legal.signupCountryNotServed")}</p>
                    ) : (
                      <ResidenceNotice id={COUNTRY_NOTICE_ID} country={country} />
                    )}

                    <LegalCheckbox
                      id="register-legal"
                      checked={legalOk}
                      onChange={(e) => setLegalOk(e.target.checked)}
                      describedBy={fieldA11y("legal")["aria-describedby"]}
                      invalid={fieldA11y("legal")["aria-invalid"]}
                    >
                      <SignupConsentLabel docLang={docLangFor(country)} />
                    </LegalCheckbox>

                    {error && (
                      <div className="cn-error" role="alert" id="register-error">
                        {error}
                      </div>
                    )}

                    {/* CODE BELLOW FOR WHEN CHECKING PASSWORD STRENGH */}

                    <button className="cn-btn-primary cn-btn-block cn-btn-arrow" type="submit">
                      {t("register.createAccount")}
                    </button>
                  </form>
                </div>
              </Col>
            </Row>
          </Container>
        </section>
      </main>
      <MainFooter />
    </>
  );
};

export default Register;
